import { ActionTree } from 'vuex';
import serviceHub from '@/services/hub';
import facebookActions from './facebook/actions';
import youtubeActions from './youtube/actions';

const liveSocialActions: ActionTree<Store.LiveSocial.State, RootState> = {
  async createService({ commit, dispatch }, payload: Services.LiveSocial.PostService) {
    return serviceHub.liveSocial.postService(payload)
      .then(({ data }) => {
        if (payload.provider === 'custom' || payload.provider === 'twitch') {
          commit('setService', {
            ...data.data,
            provider_settings: JSON.parse(data.data.provider_settings),
          } as Store.LiveSocial.LiveSocialNetwork);
        }

        return data.data;
      })
      .catch((error) => {
        console.error('Action - "liveSocial/createService" ', error);

        dispatch(
          'notification/create', {
            text: 'error createService()',
            type: 'error',
          }, { root: true },
        );

        return Promise.reject(error);
      });
  },
  async createUserAccount({ dispatch }, payload: Services.LiveSocial.PostUserAccount) {
    return serviceHub.liveSocial.postUserAccount(payload)
      .then(({ data }) => ({ ...data.data, user_data: JSON.parse(data.data.user_data) }))
      .catch((error) => {
        console.error('Action - "liveSocial/createUserAccount" ', error);

        dispatch(
          'notification/create', {
            text: 'error_adding_account',
            type: 'error',
          }, { root: true },
        );

        return Promise.reject();
      });
  },
  async createWowzaTarget(_, payload: Services.LiveSocial.PostWowzaTarget) {
    return serviceHub.liveSocial.postWowzaTarget(payload).then(({ data }) => data);
  },
  async deleteService({ commit, dispatch }, payload: Store.LiveSocial.Actions.DeleteService) {
    return serviceHub.liveSocial.deleteService(payload.serviceId)
      .then(({ data }) => {
        if (data.data === 1) {
          commit('removeService', payload);
        }
      })
      .catch((error) => {
        console.error('Action - "liveSocial/deleteService" ', error);

        dispatch(
          'notification/create', {
            text: 'error deleteService()',
            type: 'error',
          }, { root: true },
        );

        return Promise.reject(error);
      });
  },
  async deleteUserAccount(
    { dispatch, commit },
    payload: Store.LiveSocial.Actions.DeleteUserAccount,
  ) {
    return serviceHub.liveSocial.deleteUserAccount(payload.accountId)
      .then(({ data }) => {
        if (data.data === 1) {
          commit('removeUserAccount', payload);
        }
      })
      .catch((error) => {
        console.error('Action - "liveSocial/deleteUserAccount" ', error);

        dispatch(
          'notification/create', {
            text: 'error deleteUserAccount()',
            type: 'error',
          }, { root: true },
        );
      });
  },
  async deleteWowzaTarget(_, payload: Services.LiveSocial.DeleteWowzaTarget) {
    return serviceHub.liveSocial.deleteWowzaTarget(payload).then(({ data }) => data);
  },
  async loadLiveSocialGeneral(
    { commit, dispatch },
    payload: Services.LiveSocial.GetLiveSocialGeneral,
  ) {
    return serviceHub.liveSocial.getLiveSocialGeneral(payload)
      .then(({ data }) => {
        commit('setLiveSocialGeneral', data.data);
      })
      .catch((error) => {
        console.error('Action - "liveSocial/loadLiveSocialGeneral" ', error);

        dispatch(
          'notification/create', {
            text: 'error_fetching_live_data',
            type: 'error',
          }, { root: true },
        );
      });
  },
  async loadServices({ commit, dispatch }, payload: Services.LiveSocial.GetServices) {
    return serviceHub.liveSocial.getServices(payload)
      .then(({ data }) => {
        const services = data.data.map((service) => ({
          ...service,
          provider_settings: JSON.parse(service.provider_settings),
          selected: !!service.status,
        }));

        commit('setServices', {
          provider: payload.provider,
          services,
        } as Store.LiveSocial.Mutations.SetServices);
      })
      .catch((error) => {
        console.error('Action - "liveSocial/loadServices" ', error);

        dispatch(
          'notification/create', {
            text: 'error loadServices()',
            type: 'error',
          }, { root: true },
        );
      });
  },
  async loadUserAccountByUserId(
    { dispatch },
    payload: Services.LiveSocial.GetUserAccountByUserId,
  ) {
    return serviceHub.liveSocial.getUserAccountByUserId(payload)
      .then(({ data }) => ({ ...data.data }))
      .catch((error) => {
        console.error('Action - "loadUserAccountByUserId" ', error);

        dispatch(
          'notification/create', {
            text: 'error loadUserAccountByUserId()',
            type: 'error',
          }, { root: true },
        );
      });
  },
  async loadUserAccounts(
    { dispatch, commit },
    payload: Services.LiveSocial.GetUserAccounts,
  ) {
    return serviceHub.liveSocial.getUserAccounts(payload)
      .then(({ data }) => {
        const accounts = data.data.map((account) => ({
          ...account,
          lives: account.lives?.map((service) => ({
            ...service,
            provider_settings: JSON.parse(service.provider_settings),
            selected: !!service.status,
          })),
          user_data: JSON.parse(account.user_data),
        })) as Store.LiveSocial.UserSocialNetwork[];

        commit('setUserAccounts', {
          accounts,
          provider: payload.provider,
        } as Store.LiveSocial.Mutations.SetUserAccounts);

        return data;
      })
      .catch((error) => {
        console.error('Action - "liveSocial/loadUserAccounts" ', error);

        dispatch(
          'notification/create', {
            text: 'error loadUserAccounts()',
            type: 'error',
          }, { root: true },
        );
      });
  },
  async saveLiveSocialGeneral(
    { commit, dispatch },
    payload: Services.LiveSocial.PostLiveSocialGeneral,
  ) {
    return serviceHub.liveSocial.postLiveSocialGeneral(payload)
      .then(({ data }) => {
        commit('setLiveSocialGeneral', data.data);
      })
      .catch((error) => {
        console.error('Action - "liveSocial/saveLiveSocialGeneral" ', error);

        dispatch(
          'notification/create', {
            text: 'error_saving_live_data',
            type: 'error',
          }, { root: true },
        );
      });
  },
  async updateLiveSocialGeneral(
    { commit, dispatch },
    payload: Services.LiveSocial.PutLiveSocialGeneral,
  ) {
    return serviceHub.liveSocial.putLiveSocialGeneral(payload)
      .then(({ data }) => {
        // data.data === 1, significa que os dados foram realmente alterados
        if (data.data === 1) {
          commit('setLiveSocialGeneral', payload);
        }
      })
      .catch((error) => {
        console.error('Action - "liveSocial/updateLiveSocialGeneral" ', error);

        dispatch(
          'notification/create', {
            text: 'error_updating_live_data',
            type: 'error',
          }, { root: true },
        );
      });
  },
  async updateService({ commit, dispatch }, payload: Store.LiveSocial.LiveSocialNetwork) {
    return serviceHub.liveSocial.putService({
      ...payload,
      provider_settings: JSON.stringify(payload.provider_settings),
    })
      .then(({ data }) => {
        commit('updateService', payload);

        return data;
      })
      .catch((error) => {
        console.error('Action - "liveSocial/updateService" ', error);

        dispatch(
          'notification/create', {
            text: 'error_updating_live_data',
            type: 'error',
          }, { root: true },
        );

        return Promise.reject(error);
      });
  },
  async saveYoutubeLivestreamUserSettings(
    { commit },
    payload: Store.LiveSocial.YoutubeLivestreamUserSettings,
  ) {
    commit('setYoutubeLivestreamUserSettings', payload);
  },
  ...facebookActions,
  ...youtubeActions,
};

export default liveSocialActions;
